import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Add as AddPosIcon } from '@mui/icons-material';
import CajaForm from './CajaForm';
import SucursalList from './SucursalList';
import { sucursalLocalApi } from './sucursalLocalApi';

const GasPanel = ({ empresa, isVisible }) => {
  const [sucursales, setSucursales] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCajaForm, setShowCajaForm] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (isVisible) {
      cargarSucursales();
      cargarCajas();
    }
  }, [isVisible, empresa.apiBaseUrl]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const cargarSucursales = async () => {
    try {
      const response = await axios.get(`${empresa.apiBaseUrl}/api/qr/mp/stores`, {
        params: { empresa: empresa.mpEmpresa }
      });
      setSucursales(response.data.results || []);
    } catch (err) {
      console.error(`Error al cargar sucursales de ${empresa.nombre}:`, err);
    }
  };

  const cargarCajas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${empresa.apiBaseUrl}/api/qr/mp/pos`, {
        params: { empresa: empresa.mpEmpresa }
      });
      // Filtrar cajas por empresa
      const todasLasCajas = response.data.results || [];      
      const cajasDeEstaEmpresa = todasLasCajas.filter(caja => 
        caja.empresa === empresa.nombre || !caja.empresa
      );
      setCajas(cajasDeEstaEmpresa);
    } catch (err) {
      showSnackbar(`Error al cargar cajas de ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const crearCaja = async (cajaData) => {
    setLoading(true);
    try {
      // Código de empresa que espera el backend de MP (1=HIPER, 2=NATURAL)
      const cajaParaMP = {
        ...cajaData,
        empresa: empresa.mpEmpresa
      };

      const response = await axios.post(`${empresa.apiBaseUrl}/api/qr/mp/pos`, cajaParaMP);
      
      if (response.data && response.data.id) {
        await sucursalLocalApi.guardarCajaLocal({
          id: response.data.id,
          name: response.data.name,
          external_id: response.data.external_id,
          sucursal_id: cajaData.store_id,
          usuario: localStorage.getItem(empresa.localStorageKey) || localStorage.getItem("user"),
          empresa: empresa.nombre
        });
      }
      
      showSnackbar(`Caja "${cajaData.name}" creada exitosamente en ${empresa.nombre}`);
      await cargarCajas();
      setShowCajaForm(false);
      setSelectedSucursal(null);
      return response.data;
    } catch (err) {
      showSnackbar(`Error al crear caja en ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const eliminarCaja = async (posId) => {
    setLoading(true);
    try {
      await axios.delete(`${empresa.apiBaseUrl}/api/qr/mp/pos/${posId}`);
      await sucursalLocalApi.eliminarCajaLocal(posId);
      showSnackbar(`Caja eliminada exitosamente de ${empresa.nombre}`);
      await cargarCajas();
    } catch (err) {
      showSnackbar(`Error al eliminar caja de ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const actualizarCaja = async (posId, updateDataMP, updateDataLocal) => {
    setLoading(true);
    try {
      const response = await axios.put(`${empresa.apiBaseUrl}/api/qr/mp/pos/${posId}`, {
        ...updateDataMP,
        empresa: empresa.mpEmpresa
      });
      
      if (response.data && updateDataLocal) {
        await sucursalLocalApi.actualizarCajaLocal(posId, updateDataLocal);
      }
      
      showSnackbar(`Caja actualizada exitosamente en ${empresa.nombre}`);
      await cargarCajas();
      return response.data;
    } catch (err) {
      showSnackbar(`Error al actualizar caja en ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const formatearExternalId = (nombre) => {
    return nombre
      .toUpperCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9]/g, '_')
      .slice(0, 40);
  };

  const recargarTodo = async () => {
    await cargarSucursales();
    await cargarCajas();
  };

  if (!isVisible) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="600" sx={{ color: empresa.color }}>
          Gestión de Cajas - {empresa.nombre}
        </Typography>
        {!showCajaForm && (
          <button 
            onClick={() => setShowCajaForm(true)}
            style={{
              backgroundColor: empresa.color,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <AddPosIcon /> Nueva Caja
          </button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: empresa.color }} />
        </Box>
      )}

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        {showCajaForm ? (
          <CajaForm
            onCreate={crearCaja}
            onCancel={() => {
              setShowCajaForm(false);
              setSelectedSucursal(null);
            }}
            sucursales={sucursales}
            selectedSucursal={selectedSucursal}
            formatearExternalId={formatearExternalId}
            empresa={empresa.id}
          />
        ) : (
          <SucursalList
            sucursales={sucursales}
            cajas={cajas}
            onEliminarCaja={eliminarCaja}
            onActualizarCaja={actualizarCaja}
            onSeleccionarSucursal={(sucursal) => {
              setSelectedSucursal(sucursal);
              setShowCajaForm(true);
            }}
            onRecargar={recargarTodo}
            onEliminarSucursal={() => {}}
            onActualizarSucursal={() => {}}
          />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GasPanel;



// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import {
//   Box,
//   Typography,
//   Alert,
//   Snackbar,
//   CircularProgress,
//   Paper,
// } from '@mui/material';
// import { Add as AddPosIcon } from '@mui/icons-material';
// import CajaForm from './CajaForm';
// import SucursalList from './SucursalList';
// import { sucursalLocalApi } from './sucursalLocalApi';

// const GasPanel = ({ empresa, isVisible }) => {
//   const [sucursales, setSucursales] = useState([]);
//   const [cajas, setCajas] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [showCajaForm, setShowCajaForm] = useState(false);
//   const [selectedSucursal, setSelectedSucursal] = useState(null);
//   const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

//   useEffect(() => {
//     if (isVisible) {
//       cargarSucursales();
//       cargarCajas();
//     }
//   }, [isVisible, empresa.apiBaseUrl]);

//   const showSnackbar = (message, severity = 'success') => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const cargarSucursales = async () => {
//     try {
//       const response = await axios.get(`${empresa.apiBaseUrl}/api/mp/stores`, {
//         params: { empresa: empresa.mpEmpresa }
//       });
//       setSucursales(response.data.results || []);
//     } catch (err) {
//       console.error(`Error al cargar sucursales de ${empresa.nombre}:`, err);
//     }
//   };

//   const cargarCajas = async () => {
//     setLoading(true);
//     try {
//       const response = await axios.get(`${empresa.apiBaseUrl}/api/mp/pos`, {
//         params: { empresa: empresa.mpEmpresa }
//       });
//       // Filtrar cajas por empresa
//       const todasLasCajas = response.data.results || [];      
//       const cajasDeEstaEmpresa = todasLasCajas.filter(caja => 
//         caja.empresa === empresa.nombre || !caja.empresa
//       );
//       setCajas(cajasDeEstaEmpresa);
//     } catch (err) {
//       showSnackbar(`Error al cargar cajas de ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const crearCaja = async (cajaData) => {
//     setLoading(true);
//     try {
//       // Código de empresa que espera el backend de MP (1=HIPER, 2=NATURAL)
//       const cajaParaMP = {
//         ...cajaData,
//         empresa: empresa.mpEmpresa
//       };

//       const response = await axios.post(`${empresa.apiBaseUrl}/api/mp/pos`, cajaParaMP);
      
//       if (response.data && response.data.id) {
//         await sucursalLocalApi.guardarCajaLocal({
//           id: response.data.id,
//           name: response.data.name,
//           external_id: response.data.external_id,
//           sucursal_id: cajaData.store_id,
//           usuario: localStorage.getItem(empresa.localStorageKey) || localStorage.getItem("user"),
//           empresa: empresa.nombre
//         });
//       }
      
//       showSnackbar(`Caja "${cajaData.name}" creada exitosamente en ${empresa.nombre}`);
//       await cargarCajas();
//       setShowCajaForm(false);
//       setSelectedSucursal(null);
//       return response.data;
//     } catch (err) {
//       showSnackbar(`Error al crear caja en ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
//       throw err;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const eliminarCaja = async (posId) => {
//     setLoading(true);
//     try {
//       await axios.delete(`${empresa.apiBaseUrl}/api/mp/pos/${posId}`);
//       await sucursalLocalApi.eliminarCajaLocal(posId);
//       showSnackbar(`Caja eliminada exitosamente de ${empresa.nombre}`);
//       await cargarCajas();
//     } catch (err) {
//       showSnackbar(`Error al eliminar caja de ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const actualizarCaja = async (posId, updateDataMP, updateDataLocal) => {
//     setLoading(true);
//     try {
//       const response = await axios.put(`${empresa.apiBaseUrl}/api/mp/pos/${posId}`, {
//         ...updateDataMP,
//         empresa: empresa.mpEmpresa
//       });
      
//       if (response.data && updateDataLocal) {
//         await sucursalLocalApi.actualizarCajaLocal(posId, updateDataLocal);
//       }
      
//       showSnackbar(`Caja actualizada exitosamente en ${empresa.nombre}`);
//       await cargarCajas();
//       return response.data;
//     } catch (err) {
//       showSnackbar(`Error al actualizar caja en ${empresa.nombre}: ${err.response?.data?.message || err.message}`, 'error');
//       throw err;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatearExternalId = (nombre) => {
//     return nombre
//       .toUpperCase()
//       .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
//       .replace(/[^A-Z0-9]/g, '_')
//       .slice(0, 40);
//   };

//   const recargarTodo = async () => {
//     await cargarSucursales();
//     await cargarCajas();
//   };

//   if (!isVisible) return null;

//   return (
//     <Box>
//       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//         <Typography variant="h6" fontWeight="600" sx={{ color: empresa.color }}>
//           Gestión de Cajas - {empresa.nombre}
//         </Typography>
//         {!showCajaForm && (
//           <button 
//             onClick={() => setShowCajaForm(true)}
//             style={{
//               backgroundColor: empresa.color,
//               color: 'white',
//               border: 'none',
//               padding: '8px 16px',
//               borderRadius: '8px',
//               cursor: 'pointer',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '8px'
//             }}
//           >
//             <AddPosIcon /> Nueva Caja
//           </button>
//         )}
//       </Box>

//       {loading && (
//         <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
//           <CircularProgress sx={{ color: empresa.color }} />
//         </Box>
//       )}

//       <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
//         {showCajaForm ? (
//           <CajaForm
//             onCreate={crearCaja}
//             onCancel={() => {
//               setShowCajaForm(false);
//               setSelectedSucursal(null);
//             }}
//             sucursales={sucursales}
//             selectedSucursal={selectedSucursal}
//             formatearExternalId={formatearExternalId}
//             empresa={empresa.id}
//           />
//         ) : (
//           <SucursalList
//             sucursales={sucursales}
//             cajas={cajas}
//             onEliminarCaja={eliminarCaja}
//             onActualizarCaja={actualizarCaja}
//             onSeleccionarSucursal={(sucursal) => {
//               setSelectedSucursal(sucursal);
//               setShowCajaForm(true);
//             }}
//             onRecargar={recargarTodo}
//             onEliminarSucursal={() => {}}
//             onActualizarSucursal={() => {}}
//           />
//         )}
//       </Paper>

//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={6000}
//         onClose={() => setSnackbar({ ...snackbar, open: false })}
//         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//       >
//         <Alert 
//           onClose={() => setSnackbar({ ...snackbar, open: false })} 
//           severity={snackbar.severity}
//           sx={{ width: '100%' }}
//         >
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default GasPanel;